import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from "@angular/core";
import {
  HomeCalendarDay,
  DecisionPage,
  DecisionState,
  HomeCalendarGranularity,
  HomeCalendarMonthSummary,
  HomeCalendarViewModel
} from "../../../models/home";
import { UxError } from "../../../models/ux-error.model";
import { LanguageService } from "../../../services/language/language.service";
import { CalendarAdapter } from "../adapters/calendar.adapter";
import { formatDecisionTitle } from "../../../utils/decision-title-format.util";

@Component({
  selector: "app-home-decision-calendar",
  templateUrl: "./home-decision-calendar.component.html",
  styleUrls: [
    "./home-decision-calendar.component.shell-and-grid.scss",
    "./home-decision-calendar.component.entries-and-responsive.scss"
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeDecisionCalendarComponent {
  private selectedDayKey: string | null = null;
  private pendingPanelScroll = false;
  private readonly stateOrder: readonly DecisionState[] = [
    "act_now",
    "act_soon",
    "waiting",
    "act_without_rush",
    "completed"
  ];

  @ViewChild("dayPanel") private dayPanel?: ElementRef<HTMLElement>;

  @Input() page: DecisionPage | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Input() selectedDecisionId: string | null = null;
  @Input() granularity: HomeCalendarGranularity = "month";
  @Input() anchorDate = CalendarAdapter.toDateKey(new Date());

  @Output() retryRequest = new EventEmitter<void>();
  @Output() selectDecision = new EventEmitter<string>();
  @Output() granularityChange = new EventEmitter<HomeCalendarGranularity>();
  @Output() periodShift = new EventEmitter<number>();
  @Output() todayRequest = new EventEmitter<void>();
  @Output() anchorDateChange = new EventEmitter<string>();

  readonly granularities: readonly HomeCalendarGranularity[] = ["week", "month", "year"];
  readonly formatDecisionTitle = formatDecisionTitle;

  constructor(public readonly lang: LanguageService) {}

  ngAfterViewChecked(): void {
    if (!this.pendingPanelScroll || !this.dayPanel?.nativeElement) {
      return;
    }

    this.pendingPanelScroll = false;
    this.dayPanel.nativeElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  get calendarViewModel() {
    return CalendarAdapter.toViewModel(this.page, this.granularity, this.anchorDate, this.selectedDecisionId);
  }

  /**
   * Vista de agenda para mobile: siempre acotada al mes del `anchorDate`, independientemente de la
   * granularidad activa en desktop (semana/mes/año). Mantiene una única fuente de datos y evita que
   * el selector compacto de mes quede desincronizado con la granularidad de escritorio.
   */
  get agendaViewModel(): HomeCalendarViewModel {
    return CalendarAdapter.toViewModel(this.page, "month", this.anchorDate, this.selectedDecisionId);
  }

  /** Días del mes con al menos una decisión, en orden cronológico (para la lista de agenda). */
  get agendaDays(): readonly HomeCalendarDay[] {
    return this.agendaViewModel.days.filter((day) => day.inCurrentPeriod && day.entries.length > 0);
  }

  get agendaMonthTitle(): string {
    return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(
      this.agendaViewModel.anchorDate
    );
  }

  /** Máximo de chips de estado visibles por celda del grid mensual antes de mostrar "+N más". */
  private readonly maxVisibleDayStates = 3;

  get selectedDay(): HomeCalendarDay | null {
    const viewModel = this.calendarViewModel;
    const explicitDay = this.selectedDayKey
      ? viewModel.days.find((day) => day.dayKey === this.selectedDayKey && day.entries.length > 0) ?? null
      : null;

    if (explicitDay) {
      return explicitDay;
    }

    if (this.selectedDecisionId) {
      return viewModel.days.find((day) => day.entries.some((entry) => entry.decisionId === this.selectedDecisionId)) ?? null;
    }

    return null;
  }

  get periodTitle(): string {
    const viewModel = this.calendarViewModel;

    if (this.granularity === "week" && viewModel.days.length >= 7) {
      const formatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
      const start = formatter.format(viewModel.days[0].date);
      const end = formatter.format(viewModel.days[viewModel.days.length - 1].date);
      return `${start} - ${end}`;
    }

    if (this.granularity === "year") {
      return new Intl.DateTimeFormat(undefined, { year: "numeric" }).format(viewModel.anchorDate);
    }

    return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(viewModel.anchorDate);
  }

  onRetry(): void {
    this.retryRequest.emit();
  }

  onSelectGranularity(granularity: HomeCalendarGranularity): void {
    if (granularity === this.granularity) {
      return;
    }

    if (granularity === "week" && this.selectedDay?.dayKey) {
      this.anchorDateChange.emit(this.selectedDay.dayKey);
    }

    this.granularityChange.emit(granularity);
  }

  onNavigatePeriod(direction: number): void {
    this.periodShift.emit(direction);
  }

  onJumpToToday(): void {
    this.todayRequest.emit();
  }

  /**
   * Navegación del selector compacto de la agenda mobile. Desplaza SIEMPRE por mes (no por la
   * granularidad de desktop) para mantener la agenda coherente con `agendaViewModel`.
   */
  onAgendaNavigate(direction: number): void {
    this.anchorDateChange.emit(CalendarAdapter.shiftAnchorDate(this.anchorDate, "month", direction));
  }

  onSelectDecision(decisionId: string): void {
    this.selectDecision.emit(decisionId);
  }

  onSelectDay(dayKey: string, hasEntries: boolean): void {
    if (!hasEntries) {
      return;
    }

    const nextDayKey = this.selectedDayKey === dayKey ? null : dayKey;
    this.selectedDayKey = nextDayKey;
    this.pendingPanelScroll = !!nextDayKey;
  }

  onSelectDayEntry(event: Event, dayKey: string, decisionId: string): void {
    event.stopPropagation();
    this.selectedDayKey = dayKey;
    this.pendingPanelScroll = true;
    this.onSelectDecision(decisionId);
  }

  openMonth(month: HomeCalendarMonthSummary): void {
    this.selectedDayKey = month.previewDays[0]?.dayKey ?? null;
    this.pendingPanelScroll = false;
    this.granularityChange.emit("month");
    this.anchorDateChange.emit(month.monthKey);
  }

  getStateLabel(state: DecisionState): string {
    return this.lang.translate(`home.decisionStates.${state}`);
  }

  getStateIconClass(state: DecisionState): string {
    switch (state) {
      case "act_now":
        return "fa-bolt";
      case "act_soon":
        return "fa-clock";
      case "waiting":
        return "fa-pause-circle";
      case "completed":
        return "fa-check-circle";
      case "act_without_rush":
      default:
        return "fa-info-circle";
    }
  }

  getDayStateSummaries(day: HomeCalendarDay): ReadonlyArray<{ state: DecisionState; count: number }> {
    const summaries: Array<{ state: DecisionState; count: number }> = [];

    for (const state of this.stateOrder) {
      const count = day.entries.filter((entry) => entry.state === state).length;

      if (count) {
        summaries.push({ state, count });
      }
    }

    return summaries;
  }

  getStateCountLabel(state: DecisionState, count: number): string {
    return `${this.getStateLabel(state)}: ${count}`;
  }

  /** Chips de estado visibles en la celda del grid mensual, acotados para mantener alturas parejas. */
  getVisibleDayStateSummaries(day: HomeCalendarDay): ReadonlyArray<{ state: DecisionState; count: number }> {
    return this.getDayStateSummaries(day).slice(0, this.maxVisibleDayStates);
  }

  /** Nº de grupos de estado que no caben en la celda; alimenta el indicador "+N más". */
  getDayStateOverflowCount(day: HomeCalendarDay): number {
    return Math.max(0, this.getDayStateSummaries(day).length - this.maxVisibleDayStates);
  }

  getMonthStateKeys(month: HomeCalendarMonthSummary): readonly DecisionState[] {
    return this.stateOrder.filter((state) => Boolean(month.byState[state]));
  }

  getMonthStateCount(month: HomeCalendarMonthSummary, state: DecisionState): number {
    return month.byState[state] ?? 0;
  }

  trackDay(_: number, day: { dayKey: string }): string {
    return day.dayKey;
  }

  trackEntry(_: number, entry: { decisionId: string }): string {
    return entry.decisionId;
  }

  trackMonth(_: number, month: HomeCalendarMonthSummary): string {
    return month.monthKey;
  }
}
